import React from "react";
import { CSVLink } from "react-csv";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { Spinner } from "../../components/UI/States";
import { formatMinutes, jobLabel } from "./pcwApi";

// Building blocks shared by the shop clock dashboard (PCWDashboard.js) and
// its Schedule tab (ShopSchedule.js).

export const DATE_INPUT = "app-input h-10 rounded-xl border px-3 text-sm";

// Segmented control — the same look the MUI theme gives ToggleButtonGroup.
export const SEGMENT_TRACK =
  "flex w-fit max-w-full gap-0.5 overflow-x-auto rounded-xl bg-[color:var(--app-hover)] p-[3px]";
export const segmentClass = (active, size = "sm") =>
  `inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-[9px] font-semibold transition ${
    size === "md" ? "px-3.5 py-1.5 text-sm" : "px-3 py-[5px] text-xs"
  } ${
    active
      ? "bg-white text-[color:var(--app-text)] shadow-sm dark:bg-white/15"
      : "app-muted hover:text-[color:var(--app-text)]"
  }`;

export const csvHours = (minutes) => ((minutes || 0) / 60).toFixed(2);

export const errorBanner = (message) => (
  <div
    role="alert"
    className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-300"
  >
    {message}
  </div>
);

// Marks a job a worker created on the shop tablet (no Bistrack work order).
export const CustomJobChip = ({ finished }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
      finished
        ? "bg-slate-500/15 text-slate-600 dark:text-slate-300"
        : "bg-amber-500/15 text-amber-700 dark:text-amber-300"
    }`}
  >
    {finished ? "Custom job · finished" : "Custom job"}
  </span>
);

// A job in a table: "WO 138447" over its Bistrack name, or a custom job's
// name over its chip.
export const JobCell = ({ workOrder, finished }) => {
  const job = jobLabel(workOrder);
  return (
    <>
      <div className={`font-medium ${job.custom ? "" : "tabular-nums"}`}>
        {job.title}
      </div>
      {job.custom ? (
        <div className="mt-0.5">
          <CustomJobChip finished={finished} />
        </div>
      ) : (
        job.detail && (
          <div className="app-muted truncate text-xs">{job.detail}</div>
        )
      )}
    </>
  );
};

// Matches the shop floor TV: amber from 90% of the allotted time, red once
// it's over.
export const allottedState = (used, allotted) => {
  const pct = (used / allotted) * 100;
  return { pct, tone: pct > 100 ? "over" : pct >= 90 ? "warn" : "ok" };
};

const ALLOTTED_TONES = {
  ok: { bar: "bg-sky-500", text: "app-muted" },
  warn: { bar: "bg-amber-500", text: "text-amber-700 dark:text-amber-300" },
  over: { bar: "bg-rose-500", text: "text-rose-700 dark:text-rose-300" },
};

export const AllottedBar = ({ used, allotted, className = "" }) => {
  const { pct, tone } = allottedState(used, allotted);
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="h-1.5 min-w-[72px] flex-1 overflow-hidden rounded-full bg-slate-500/15">
        <div
          className={`h-full rounded-full ${ALLOTTED_TONES[tone].bar}`}
          style={{ width: `${Math.max(1, Math.min(100, pct))}%` }}
        />
      </div>
      <span
        className={`w-11 text-right text-xs font-semibold tabular-nums ${ALLOTTED_TONES[tone].text}`}
      >
        {Math.floor(pct)}%
      </span>
    </div>
  );
};

// "27h 30m left" / "3h 10m over"
export const allottedRemaining = (used, allotted) =>
  used > allotted
    ? `${formatMinutes(used - allotted)} over`
    : `${formatMinutes(allotted - used)} left`;

export const fileSlug = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

// Small download chip for a table header; renders nothing with no rows.
export const CsvButton = ({ data, headers, filename }) => {
  if (!data || data.length === 0) return null;
  return (
    <CSVLink
      data={data}
      headers={headers}
      filename={filename}
      className="app-btn app-btn-secondary app-btn-sm !gap-1"
    >
      <DownloadOutlinedIcon sx={{ fontSize: 15 }} />
      CSV
    </CSVLink>
  );
};

// A titled card around a table. The table scrolls sideways on narrow
// screens; `footer` (pagination) stays put below it.
export const SectionCard = ({ title, subtitle, action, footer, children }) => (
  <section className="app-card overflow-hidden rounded-2xl border">
    <div className="app-divider flex flex-wrap items-center justify-between gap-3 border-b px-4 py-4 sm:px-5">
      <div className="min-w-0">
        <h3 className="font-semibold tracking-tight">{title}</h3>
        {subtitle && <p className="app-muted mt-0.5 text-xs">{subtitle}</p>}
      </div>
      {action && (
        <div className="flex flex-wrap items-center gap-2">{action}</div>
      )}
    </div>
    <div className="overflow-x-auto">{children}</div>
    {footer}
  </section>
);

export const HeadRow = ({ columns }) => (
  <thead>
    <tr>
      {columns.map((column, index) => (
        <th key={`${column}-${index}`} className="px-4">
          {column}
        </th>
      ))}
    </tr>
  </thead>
);

export const SortableTh = ({ label, column, sortKey, sortDir, onSort }) => (
  <th
    className="px-4"
    aria-sort={
      sortKey === column
        ? sortDir === "asc"
          ? "ascending"
          : "descending"
        : undefined
    }
  >
    <button
      type="button"
      onClick={() => onSort(column)}
      className={`-mx-1 inline-flex items-center gap-1 rounded-md px-1 transition-colors hover:text-[color:var(--app-text)] ${
        sortKey === column ? "app-accent" : ""
      }`}
    >
      {label}
      {sortKey === column &&
        (sortDir === "asc" ? (
          <ArrowUpwardIcon sx={{ fontSize: 13 }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 13 }} />
        ))}
    </button>
  </th>
);

export const MessageRow = ({ colSpan, children }) => (
  <tr>
    <td colSpan={colSpan} className="app-muted px-4 py-10 text-center">
      {children}
    </td>
  </tr>
);

export const LoadingRow = ({ colSpan, children }) => (
  <MessageRow colSpan={colSpan}>
    <span className="inline-flex items-center gap-2.5">
      <Spinner size={18} />
      {children}
    </span>
  </MessageRow>
);
